/**
 * What the map calls each site status.
 *
 * The map says the same word for a status wherever it appears — the filter
 * chip, the list row's pill, the marker popover and the site detail header —
 * and every one of those words is already the app's: the site grid's status
 * column says "Alarming", and "Offline" / "Online" / "No Data" are the general
 * vocabulary the dashboards and radio surfaces use. The map owns no spelling of
 * its own, so an operator moving between screens reads one vocabulary.
 *
 * Returns plain JS — no JSX, no redux.
 */
import { SITE_STATUS } from '@sophocles/features/map/mapState';

/** Each status's i18n key and its English fallback. */
export const SITE_STATUS_LABEL = Object.freeze({
  [SITE_STATUS.ALARMING]: Object.freeze(['unitStatus.alarming', 'Alarming']),
  [SITE_STATUS.OFFLINE]: Object.freeze(['general.offline', 'Offline']),
  // A site with no unit, no radio or no check-in ever has nothing to report.
  [SITE_STATUS.SILENT]: Object.freeze(['general.noData', 'No Data']),
  [SITE_STATUS.ONLINE]: Object.freeze(['general.online', 'Online']),
});

/**
 * The translated label for one site status.
 *
 * @param {Function} t - the i18next translator.
 * @param {string} status - a SITE_STATUS value; anything else reads as online.
 * @returns {string} the label to show.
 */
export function siteStatusLabel(t, status) {
  const [key, fallback] = SITE_STATUS_LABEL[status] ?? SITE_STATUS_LABEL[SITE_STATUS.ONLINE];
  return t(key, fallback);
}
